import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { type Action, Store } from '@ngrx/store';

import { switchMap, withLatestFrom } from 'rxjs/operators';

import { getUuid } from '@common/helpers';

import type { NormalizedColumnEntity, NormalizedGridEntity, NormalizedTabEntity } from '@web/types';

import { selectColumnsEntities } from '@web/store/page-structure/columns/columns.selectors';

import { WebWidgetsFactoryService } from '@web/core/services/web-widgets-factory.service';

import { GridsService } from '@web/store/page-structure/grids/grids.service';

import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { TabsFacade } from './tabs.facade';

import { HistoryChangeAction } from '@web/store/history/history.model';
import HistoryActions from '@web/store/history/history.actions';

import WidgetsLegacyActions from '../widgets/widgets.actions';
import GridsActions from '../grids/grids.actions';
import TabsActions from './tabs.actions';

@Injectable()
export class TabsEffects {
    private readonly actions$: Actions = inject(Actions);
    private readonly tabsFacade: TabsFacade = inject(TabsFacade);
    private readonly gridFacade: GridsFacade = inject(GridsFacade);
    private readonly gridService: GridsService = inject(GridsService);
    private readonly store: Store = inject(Store);

    public readonly addTab$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TabsActions.add),
            switchMap(({ entity, order }) => {
                return [
                    TabsActions.added({
                        entity: {
                            ...entity,
                            children: entity.children.map((i) => i.id),
                        },
                        order,
                    }),
                ];
            })
        )
    );

    public readonly createTab$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TabsActions.create),
            switchMap(({ parentId, order, skipHistory, meta }) => {
                const tabEntity = this.tabsFacade.getTabInstanceValue(parentId);
                const gridEntities = Array.from({ length: 3 }).map((_) =>
                    this.gridFacade.getGridInstanceValue(tabEntity.id, meta.variant ?? 0)
                );

                tabEntity.children = gridEntities.map((grid) => grid.id);
                gridEntities.forEach((grid, index) => {
                    tabEntity.props.tabs.push({
                        title: `Tab ${index + 1}`,
                        isActive: index === 0,
                        contentEntityId: grid.id,
                    });
                });

                return [
                    GridsActions.addedMany({ entities: gridEntities }),
                    TabsActions.created({
                        entity: tabEntity,
                        order,
                        skipHistory,
                        childEntity: {
                            ...tabEntity,
                            children: gridEntities.map((grid) => ({
                                ...grid,
                                children: grid.children.map(() => []),
                            })),
                        },
                    }),
                ];
            })
        )
    );

    public readonly removeTab$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TabsActions.remove),
            withLatestFrom(this.store.select(selectColumnsEntities)),
            switchMap(([{ entity, order, skipHistory }, columns]) => {
                const column = columns[entity.parentId] as NormalizedColumnEntity | undefined;
                const orderValue =
                    typeof order === 'number'
                        ? order
                        : (column?.children?.map(({ id }) => id)?.indexOf(entity.id) ?? -1);
                const children = entity.children ?? [];
                const childrenIds = children.flatMap((child) => child.children.flat().map((widget) => widget.id));

                const actions: Action[] = [
                    TabsActions.removed({
                        entity: { ...entity, children: children.map((child) => child.id) },
                        order: orderValue,
                        skipHistory: true,
                    }),
                ];

                if (childrenIds.length) {
                    actions.push(WidgetsLegacyActions.removeManyWidgets({ widgetsIds: childrenIds }));
                }

                children.forEach((child, childIndex) => {
                    actions.push(
                        GridsActions.remove({
                            entity: child,
                            order: childIndex,
                            skipHistory: true,
                        })
                    );
                });

                if (!skipHistory) {
                    // A single self-contained 'tab' entry is enough: on undo it re-adds the tab and
                    // its child grids, and on redo the remove cascades through removeTab$ again.
                    // Recording separate per-grid entries here would re-add/re-remove the grids twice.
                    actions.push(
                        HistoryActions.addBatchChangeToHistory({
                            entry: {
                                action: HistoryChangeAction.remove,
                                entityType: 'tab',
                                entity,
                                index: orderValue,
                            },
                        })
                    );
                }

                return actions;
            })
        )
    );
}
